import React, { useEffect, useState } from 'react';
import { Card, Button, Space, Alert } from 'antd';
import { Network, Maximize2, Copy, Check } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';
import { sanitizeSvg } from '../../lib/sanitizeSvg.js';

export interface MermaidSnippetProps {
  code: string;
  title?: string | undefined;
  onViewCanvas?: (() => void) | undefined;
}

export const MermaidSnippet: React.FC<MermaidSnippetProps> = ({ code, title, onViewCanvas }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [svgHtml, setSvgHtml] = useState<string>('');

  useEffect(() => {
    let isMounted = true;
    const renderDiagram = async () => {
      try {
        setError(null);
        setSvgHtml('');
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: 'neutral',
          securityLevel: 'strict',
          flowchart: { htmlLabels: false },
          fontFamily: 'Plus Jakarta Sans, Inter, sans-serif',
        });

        const id = `mermaid-${Math.random().toString(36).substring(2, 9)}`;
        const { svg } = await mermaid.render(id, code);
        if (isMounted) {
          setSvgHtml(sanitizeSvg(svg));
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : t('crm.ai_chat.mermaid.render_error'));
        }
      }
    };

    void renderDiagram();
    return () => {
      isMounted = false;
    };
  }, [code, t]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <Card
      size="small"
      style={{
        borderRadius: 12,
        borderColor: 'var(--ant-color-border)',
        background: 'var(--ant-color-bg-container)',
        overflow: 'hidden',
      }}
      styles={{ body: { padding: '12px 16px' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space size={8}>
            <Network size={16} style={{ color: 'var(--ant-color-info)' }} />
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-text)' }}>
              {title ?? t('crm.ai_chat.mermaid.default_title')}
            </span>
          </Space>
          <Space size={4}>
            <Button
              type="text"
              size="small"
              icon={
                copied ? (
                  <Check size={13} style={{ color: 'var(--ant-color-success)' }} />
                ) : (
                  <Copy size={13} />
                )
              }
              onClick={handleCopy}
              style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
            >
              {copied ? t('crm.ai_chat.artifact.copied') : t('crm.ai_chat.mermaid.code')}
            </Button>
            {onViewCanvas && (
              <Button
                type="text"
                size="small"
                icon={<Maximize2 size={13} />}
                onClick={onViewCanvas}
                style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
              >
                {t('crm.ai_chat.common.open_canvas')}
              </Button>
            )}
          </Space>
        </div>
      }
    >
      {error ? (
        <Alert
          title={t('crm.ai_chat.mermaid.syntax_error')}
          description={error}
          type="error"
          showIcon
          style={{ fontSize: 12 }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflowX: 'auto',
            padding: '8px 0',
            minHeight: 100,
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgHtml) }}
        />
      )}
    </Card>
  );
};
